import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Alert,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../services/supabaseClient';
import { apiRequest } from '../config/api';
import { Document, Language } from '../types';
import { DEFAULT_CONFIG } from '../utils/constants';
import QuickDocumentNavigation from '../components/QuickDocumentNavigation';
import PDFViewer from '../components/PDFViewer';

interface ChatMessage {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  document_id?: number | string;
  processing_time?: number;
}

interface StructuredError {
  type: 'timeout' | 'rate_limit' | 'network' | 'llm_error' | 'unknown';
  message: string;
  retry_after?: number;
  fallback_available: boolean;
}

// PDFChatResponse interface removed as it's not being used

export default function PDFAIChat() {
  const [searchParams] = useSearchParams();
  const [analysts, setAnalysts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [sectors, setSectors] = useState<any[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedAnalyst, setSelectedAnalyst] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<number | string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [language, setLanguage] = useState<Language>(DEFAULT_CONFIG.DEFAULT_LANGUAGE);
  
  // Date filtering states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateFilterType, setDateFilterType] = useState<'all' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'custom'>('all');
  
  // Chat states
  const [question, setQuestion] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // New states for suggestions and summary
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState<boolean>(false);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  
  // Enhanced error handling states
  const [structuredError, setStructuredError] = useState<StructuredError | null>(null);
  const [retryCountdown, setRetryCountdown] = useState<number>(0);
  const [showRetryButton, setShowRetryButton] = useState<boolean>(false);
  
  // PDF Highlight states (from search navigation)
  const [highlightPage, setHighlightPage] = useState<number | undefined>(undefined);
  const [highlightBbox, setHighlightBbox] = useState<number[] | undefined>(undefined);
  
  // Provider selection
  const [provider, setProvider] = useState<string>('gemini');
  // V3 RAG mode - search across all documents
  const [useV3Rag, setUseV3Rag] = useState<boolean>(false);
  // llmStatus removed (unused)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsAbortRef = useRef<AbortController | null>(null);
  const summaryAbortRef = useRef<AbortController | null>(null);
  const questionAbortRef = useRef<AbortController | null>(null);

  const fontFamily = language === 'Hebrew' 
    ? 'Segoe UI, Tahoma, Geneva, Verdana, Arial, sans-serif, "Segoe UI Hebrew", "Noto Sans Hebrew"'
    : 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';

  // Enhanced error handling with structured responses
  const handleApiError = (err: any, message: string): StructuredError => {
    console.error(message, err);
    
    let errorInfo: StructuredError = {
      type: 'unknown',
      message: message,
      fallback_available: false
    };

    if (err.name === 'AbortError') {
      errorInfo = {
        type: 'timeout',
        message: 'Request took too long. Please try again.',
        retry_after: 5,
        fallback_available: true
      };
    } else if (err.status === 429) {
      errorInfo = {
        type: 'rate_limit',
        message: 'Too many requests. Please wait a moment.',
        retry_after: 60,
        fallback_available: false
      };
    } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
      errorInfo = {
        type: 'network',
        message: 'Network error. Please check your connection.',
        retry_after: 10,
        fallback_available: true
      };
    } else if (err.message?.includes('LLM') || err.message?.includes('Gemini')) {
      errorInfo = {
        type: 'llm_error',
        message: 'AI service temporarily unavailable. Please try again.',
        retry_after: 15,
        fallback_available: true
      };
    }

    setStructuredError(errorInfo);
    setError(errorInfo.message);
    
    // Start countdown if retry_after is set
    if (errorInfo.retry_after) {
      setRetryCountdown(errorInfo.retry_after);
      setShowRetryButton(true);
      
      const countdownInterval = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowRetryButton(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return errorInfo;
  };

  // Enhanced API request with timeout and retry
  const apiRequestWithTimeout = async (
    endpoint: string,
    options: any,
    timeoutMs: number = 30000,
    abortRef?: React.MutableRefObject<AbortController | null>
  ) => {
    // Cancel any previous request for the same ref
    if (abortRef) {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
    }
    
    try {
      const response = await Promise.race([
        apiRequest(endpoint, {
          ...options,
          signal: abortRef ? abortRef.current!.signal : undefined
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
        )
      ]);
      
      return response;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('Request cancelled');
      }
      throw err;
    }
  };

  // Date filtering helper functions
  const getDateRange = (filterType: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filterType) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return { start: yesterday, end: today };
      case 'thisWeek':
        const startOfWeek = new Date(today.getTime() - today.getDay() * 24 * 60 * 60 * 1000);
        return { start: startOfWeek, end: new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000) };
      case 'lastWeek':
        const lastWeekStart = new Date(today.getTime() - (today.getDay() + 7) * 24 * 60 * 60 * 1000);
        const lastWeekEnd = new Date(lastWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        return { start: lastWeekStart, end: lastWeekEnd };
      case 'thisMonth':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start: startOfMonth, end: new Date(endOfMonth.getTime() + 24 * 60 * 60 * 1000) };
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: null, end: null };
    }
  };

  const isDocumentInDateRange = (doc: Document) => {
    if (dateFilterType === 'all') return true;
    
    const { start, end } = getDateRange(dateFilterType);
    if (!start && !end) return true;
    
    // V3: Prefer report_date, then created_at, then timestamp
    const docDate = doc.report_date ? new Date(doc.report_date) :
                   doc.created_at ? new Date(doc.created_at) : 
                   doc.timestamp ? new Date(doc.timestamp) : null;
    
    if (!docDate) return true; // If no date, include it
    
    const docDateOnly = new Date(docDate.getFullYear(), docDate.getMonth(), docDate.getDate());
    
    if (start && end) {
      return docDateOnly >= start && docDateOnly < end;
    } else if (start) {
      return docDateOnly >= start;
    } else if (end) {
      return docDateOnly < end;
    }
    
    return true;
  };

  const handleDateFilterChange = (filterType: string) => {
    setDateFilterType(filterType as any);
    if (filterType !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  // Fetch all filter data on mount
  useEffect(() => {
    supabase.from('analysts').select('*').then(({ data }) => setAnalysts(data || []));
    supabase.from('companies').select('*').then(({ data }) => setCompanies(data || []));
    supabase.from('sectors').select('*').then(({ data }) => setSectors(data || []));
    supabase.from('documents').select('id, title, filename, source, report_date, analyst_names, company_tickers, primary_ticker, sector, subsector, report_type, storage_url, url, summary_html, analyst_id, company_id, sector_id, section').then(({ data }) => setDocuments(data || []));
  }, []);

  // Handle URL params for direct document navigation and highlighting
  useEffect(() => {
    const docIdParam = searchParams.get('docId');
    const highlightPageParam = searchParams.get('highlightPage');
    const highlightBboxParam = searchParams.get('highlightBbox');
    
    // Parse highlight params
    if (highlightPageParam) {
      const page = parseInt(highlightPageParam);
      if (!isNaN(page)) {
        console.log('🔦 [Highlight] Setting page:', page);
        setHighlightPage(page);
      }
    } else {
      setHighlightPage(undefined);
    }
    
    if (highlightBboxParam) {
      const bboxParts = highlightBboxParam.split(',').map(Number);
      if (bboxParts.length === 4 && bboxParts.every(n => !isNaN(n))) {
        console.log('🔦 [Highlight] Setting bbox:', bboxParts);
        setHighlightBbox(bboxParts);
      }
    } else {
      setHighlightBbox(undefined);
    }
    
    // Debug log
    console.log('🔦 [Highlight] URL params:', { highlightPageParam, highlightBboxParam });
    
    // Handle document navigation (doc IDs are UUIDs, not integers)
    if (docIdParam && documents.length > 0) {
      const docExists = documents.find(d => String(d.id) === docIdParam);
      if (docExists && selectedDocId !== docIdParam) {
        setSelectedDocId(docIdParam);
        // Clear filters to ensure the document is visible
        setSelectedAnalyst(null);
        setSelectedCompany(null);
        setSelectedSector(null);
        setSelectedTicker(null);
        setDateFilterType('all');
      }
    }
  }, [searchParams, documents, selectedDocId]);

  // Check LLM status on mount
  useEffect(() => {
    const checkLlmStatus = async () => {
      try {
        const data = await apiRequest('/llm/status');
        // setLlmStatus(data.llm_status); // Removed unused state update
        
        // Check if Gemini is available (PDF processing requires Gemini)
        if (data.llm_status && !data.llm_status.gemini_available) {
          setError('Gemini service unavailable. PDF processing requires Gemini.');
        }
      } catch (err) {
        console.error('Failed to check LLM status:', err);
      }
    };
    
    checkLlmStatus();
  }, [provider]);

  // Filter documents when filters change (V3 compatible)
  const filteredDocuments = documents.filter(doc => {
    // Analyst filter - check analyst_names array or analyst_id
    const analystMatch = !selectedAnalyst || 
      doc.analyst_id === selectedAnalyst ||
      (doc.analyst_names && analysts.find(a => a.id === selectedAnalyst)?.name && 
       doc.analyst_names.includes(analysts.find(a => a.id === selectedAnalyst)?.name));
    
    // Company filter - check company_id
    const companyMatch = !selectedCompany || doc.company_id === selectedCompany;
    
    // Sector filter - check sector_id
    const sectorMatch = !selectedSector || doc.sector_id === selectedSector;
    
    // Ticker filter - check company_tickers array
    const tickerMatch = !selectedTicker || 
      (doc.company_tickers && doc.company_tickers.includes(selectedTicker));
    
    // Must have storage URL and pass date filter
    const hasUrl = doc.storage_url || doc.url;
    
    return analystMatch && companyMatch && sectorMatch && tickerMatch && hasUrl && isDocumentInDateRange(doc);
  });

  useEffect(() => {
    setSelectedDoc(filteredDocuments.find(d => d.id === selectedDocId) || null);
  }, [selectedDocId, filteredDocuments]);

  // Generate suggestions when a document is selected
  const generateSuggestions = useCallback(async () => {
    if (!selectedDocId) return;

    setLoadingSuggestions(true);
    setError(null);
    setStructuredError(null);

    try {
      const data = await apiRequestWithTimeout('/documents/pdf-direct-suggestions', {
        method: 'POST',
        body: JSON.stringify({
          document_id: selectedDocId ?? undefined,
          language: language,
          provider: provider
        }),
      }, 45000, suggestionsAbortRef); // 45s timeout for suggestions
      
      // Handle new structured response format
      if (data.suggestions) {
        setSuggestions(data.suggestions);
        
        // Show debug info if available and not successful
        if (data.debug && !data.success) {
          console.warn('Suggestions debug:', data.debug);
        }
      } else {
        setSuggestions([]);
      }
    } catch (err: any) {
      if (err?.message === 'Request cancelled') {
        // Silent abort to avoid error banner when user triggers another action
        return;
      }
      const errorInfo = handleApiError(err, 'Failed to generate suggestions');
      
      // If fallback is available, show basic suggestions
      if (errorInfo.fallback_available) {
        setSuggestions([
          'What are the key financial metrics discussed?',
          'What are the main risks identified?',
          'What is the investment recommendation?',
          'How does this compare to previous analysis?',
          'What are the key assumptions made?'
        ]);
      } else {
        setSuggestions([]);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [selectedDocId, language, provider]);

  // Debounced version of generateSuggestions
  const debouncedGenerateSuggestions = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      if (selectedDocId) {
        generateSuggestions();
      }
    }, 400);
  }, [generateSuggestions, selectedDocId]);

  useEffect(() => {
    if (selectedDocId) {
      debouncedGenerateSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [selectedDocId, debouncedGenerateSuggestions]);

  // Filter companies based on selected analyst and/or sector
  const filteredCompanies = companies.filter(company =>
    (!selectedAnalyst || company.analyst_id === selectedAnalyst) &&
    (!selectedSector || company.sector_id === selectedSector)
  );

  // Filter sectors based on selected analyst
  const filteredSectors = sectors.filter(sector =>
    !selectedAnalyst || companies.some(company => 
      company.sector_id === sector.id && company.analyst_id === selectedAnalyst
    )
  );

  // Filter analysts based on selected sector
  const filteredAnalysts = analysts.filter(analyst =>
    !selectedSector || companies.some(company => 
      company.analyst_id === analyst.id && company.sector_id === selectedSector
    )
  );

  // Ticker options should only include tickers from filteredCompanies
  const filteredTickerOptions = Array.from(new Set(filteredCompanies.map(c => c.ticker))).filter(Boolean);

  // When ticker changes, auto-select the corresponding company
  useEffect(() => {
    if (selectedTicker) {
      const company = companies.find(c => c.ticker === selectedTicker);
      setSelectedCompany(company ? company.id : null);
    }
  }, [selectedTicker, companies]);

  // Reset logic when filters change
  useEffect(() => {
    if (selectedAnalyst) {
      const analystCompanies = companies.filter(c => c.analyst_id === selectedAnalyst);
      
      if (selectedCompany && !analystCompanies.find(c => c.id === selectedCompany)) {
        setSelectedCompany(null);
      }
      
      if (selectedTicker && !analystCompanies.find(c => c.ticker === selectedTicker)) {
        setSelectedTicker(null);
      }
    }
  }, [selectedAnalyst, companies, selectedCompany, selectedTicker]);

  useEffect(() => {
    if (selectedSector) {
      const sectorCompanies = companies.filter(c => c.sector_id === selectedSector);
      
      if (selectedCompany && !sectorCompanies.find(c => c.id === selectedCompany)) {
        setSelectedCompany(null);
      }
      
      if (selectedTicker && !sectorCompanies.find(c => c.ticker === selectedTicker)) {
        setSelectedTicker(null);
      }
    }
  }, [selectedSector, companies, selectedCompany, selectedTicker]);

  const askQuestion = async () => {
    // V3 RAG mode doesn't require document selection
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }
    
    if (!useV3Rag && !selectedDocId) {
      setError('Please select a document or enable V3 RAG mode');
      return;
    }

    setLoading(true);
    setError(null);
    setStructuredError(null);

    try {
      let data;
      
      if (useV3Rag) {
        // Build conversation history for follow-up context
        const conversationHistory = chatHistory.flatMap(msg => [
          { role: 'user', content: msg.question },
          { role: 'assistant', content: msg.answer }
        ]);

        // Use V3 RAG - search across all documents
        data = await apiRequestWithTimeout('/v3/query', {
          method: 'POST',
          body: JSON.stringify({
            query: question,
            verbose: false,
            log: true,
            conversation_history: conversationHistory
          }),
        }, 60000, questionAbortRef);
        
        // Convert V3 response format
        data = {
          answer: data.answer,
          sources: data.sources,
          processing_time: 0
        };
      } else {
        // Use document-specific query
        data = await apiRequestWithTimeout('/documents/pdf-direct-question', {
          method: 'POST',
          body: JSON.stringify({
            document_id: selectedDocId ?? undefined,
          question: question,
          language: language,
          provider: provider
          }),
        }, 60000, questionAbortRef); // 60s timeout for questions
      }
      
      // Handle new structured response format
      const answer = data.answer || 'No answer received';
      const processingTime = data.processing_time || 0;
      
      // Add to chat history
      const newMessage: ChatMessage = {
        id: Date.now().toString(),
        question: question,
        answer: answer,
        timestamp: new Date(),
        document_id: selectedDocId ?? undefined,
        processing_time: processingTime
      };
      
      setChatHistory(prev => [newMessage, ...prev]);
      setQuestion(''); // Clear input
      
      // Show debug info if available and not successful
      if (data.debug && !data.success) {
        console.warn('Question debug:', data.debug);
      }
    } catch (err: any) {
      if (err?.message === 'Request cancelled') {
        return;
      }
      const errorInfo = handleApiError(err, 'Failed to get answer from PDF');
      
      // If fallback is available, add a fallback message
      if (errorInfo.fallback_available) {
        const fallbackMessage: ChatMessage = {
          id: Date.now().toString(),
          question: question,
          answer: language === 'Hebrew' 
            ? 'שירות זמנית לא זמין. אנא נסה שוב בעוד מספר דקות.'
            : 'Service temporarily unavailable. Please try again in a few minutes.',
          timestamp: new Date(),
          document_id: selectedDocId ?? undefined,
          processing_time: 0
        };
        setChatHistory(prev => [fallbackMessage, ...prev]);
        setQuestion('');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    if (!selectedDocId) return;

    setLoadingSummary(true);
    setError(null);
    setStructuredError(null);

    try {
      const data = await apiRequestWithTimeout('/documents/pdf-direct-summary', {
        method: 'POST',
        body: JSON.stringify({
          document_id: selectedDocId ?? undefined,
          language: language,
          provider: provider
        }),
      }, 90000, summaryAbortRef); // 90s timeout for summaries
      
      // Handle new structured response format
      const summary = data.summary || 'No summary generated';
      const processingTime = data.processing_time || 0;
      
      // Add summary to chat history
      const newMessage: ChatMessage = {
        id: Date.now().toString(),
        question: language === 'Hebrew' ? 'סיכום מקצועי למנהל תיק השקעות' : 'Professional Portfolio Manager Summary',
        answer: summary,
        timestamp: new Date(),
        document_id: selectedDocId ?? undefined,
        processing_time: processingTime
      };
      
      setChatHistory(prev => [newMessage, ...prev]);
      
      // Show debug info if available and not successful
      if (data.debug && !data.success) {
        console.warn('Summary debug:', data.debug);
      }
    } catch (err: any) {
      if (err?.message === 'Request cancelled') {
        return;
      }
      const errorInfo = handleApiError(err, 'Failed to generate summary');
      
      // If fallback is available, add a fallback message
      if (errorInfo.fallback_available) {
        const fallbackMessage: ChatMessage = {
          id: Date.now().toString(),
          question: language === 'Hebrew' ? 'סיכום מקצועי למנהל תיק השקעות' : 'Professional Portfolio Manager Summary',
          answer: language === 'Hebrew' 
            ? 'שירות הסיכום זמנית לא זמין. אנא נסה שוב בעוד מספר דקות.'
            : 'Summary service temporarily unavailable. Please try again in a few minutes.',
          timestamp: new Date(),
          document_id: selectedDocId ?? undefined,
          processing_time: 0
        };
        setChatHistory(prev => [fallbackMessage, ...prev]);
      }
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuestion(suggestion);
  };

  // Retry function for failed requests
  const handleRetry = () => {
    setError(null);
    setStructuredError(null);
    setShowRetryButton(false);
    setRetryCountdown(0);
    
    // Retry the last action based on what was loading
    if (loadingSuggestions) {
      generateSuggestions();
    } else if (loadingSummary) {
      generateSummary();
    } else if (loading) {
      askQuestion();
    }
  };

  // Helper function to detect if text contains Hebrew characters
  const containsHebrew = (text: string) => {
    return /[\u0590-\u05FF]/.test(text);
  };

  // Helper function to render text with proper RTL support
  const renderTextWithRTL = (text: string) => {
    try {
      if (!text || typeof text !== 'string') {
        return <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif' }}>No content</div>;
      }
      
      const isHebrew = containsHebrew(text);
      
      if (isHebrew) {
        // Process text to handle mixed English content properly
        const processedText = text.replace(
          /([a-zA-Z0-9\s-_.]+)/g, // Match English words, numbers, and common symbols
          (match) => {
            // Add Unicode marks for English content in Hebrew text
            return `\u2067${match}\u2069`;
          }
        ).replace(
          /\(([^)]+)\)/g, // Match content in parentheses
          (match, content) => {
            // If content contains English, add proper Unicode marks
            if (/[a-zA-Z]/.test(content)) {
              return `(\u2067${content}\u2069)`;
            }
            return match;
          }
        );
        
        return (
          <div 
            className="hebrew-text"
            style={{
              direction: 'rtl',
              textAlign: 'right',
              unicodeBidi: 'embed',
              fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',
              lineHeight: '1.8',
              fontSize: '1rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}
          >
            {processedText}
          </div>
        );
      }
      
      return (
        <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif' }}>
          {text}
        </div>
      );
    } catch (error) {
      console.error('Error rendering text with RTL:', error);
      return (
        <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif', color: 'red' }}>
          Error rendering content
        </div>
      );
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      askQuestion();
    }
  };

  const clearChat = () => {
    setChatHistory([]);
  };

  const resetFilters = () => {
    setSelectedAnalyst(null);
    setSelectedCompany(null);
    setSelectedSector(null);
    setSelectedTicker(null);
    setSelectedDocId(null);
    setSelectedDoc(null);
    setDateFilterType('all');
    setStartDate(null);
    setEndDate(null);
    clearChat();
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box 
        sx={{ 
          p: { xs: 0.5, sm: 1, md: 1.5 }, 
          fontFamily, 
          bgcolor: '#F7F9FB', 
          minHeight: '100vh',
          direction: language === 'Hebrew' ? 'rtl' : 'ltr',
          width: '100%',
          overflowX: 'hidden',
          maxWidth: '95vw',
          mx: 'auto'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '95vw', mx: 'auto' }}>
          <Typography variant="h4" gutterBottom sx={{ fontFamily, color: '#1A2341', fontWeight: 700, fontSize: { xs: 24, sm: 32 }, mb: 2 }}>
            PDF AI Chat
          </Typography>

          {/* Main Layout with Sidebar */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1, width: '100%', maxWidth: '95vw' }}>
            {/* Left Sidebar - Quick Navigation */}
            <Box sx={{ 
              width: { xs: '100%', lg: '280px' }, 
              flexShrink: 0,
              position: { xs: 'static', lg: 'sticky' },
              top: 20,
              height: 'fit-content'
            }}>
              <QuickDocumentNavigation
                onDocumentSelect={(docId) => {
                  setSelectedDocId(docId);
                  // Clear filters when selecting from quick navigation
                  setSelectedAnalyst(null);
                  setSelectedCompany(null);
                  setSelectedSector(null);
                  setSelectedTicker(null);
                }}
                selectedDocId={selectedDocId}
                language={language}
              />
            </Box>
            
            {/* Right Main Content */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: '95vw' }}>
              <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', width: '100%' }}>
                <Grid container spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                  <Grid item xs={12} sm={2}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Analyst</Typography>
                    <Select
                      value={selectedAnalyst || ''}
                      onChange={(e) => setSelectedAnalyst(e.target.value as number | null)}
                      size="small"
                      sx={{ width: '100%', fontFamily, mb: 2 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filteredAnalysts.map(analyst => (
                        <MenuItem key={analyst.id} value={analyst.id}>{analyst.name}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Company</Typography>
                    <Select
                      value={selectedCompany || ''}
                      onChange={(e) => setSelectedCompany(e.target.value as number | null)}
                      size="small"
                      sx={{ width: '100%', fontFamily, mb: 2 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filteredCompanies.map(company => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.ticker} • {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Sector</Typography>
                    <Select
                      value={selectedSector || ''}
                      onChange={(e) => setSelectedSector(e.target.value as number | null)}
                      size="small"
                      sx={{ width: '100%', fontFamily, mb: 2 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filteredSectors.map(sector => (
                        <MenuItem key={sector.id} value={sector.id}>{sector.name}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Ticker</Typography>
                    <Select
                      value={selectedTicker || ''}
                      onChange={(e) => setSelectedTicker(e.target.value as string | null)}
                      size="small"
                      sx={{ width: '100%', fontFamily, mb: 2 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filteredTickerOptions.map(ticker => (
                        <MenuItem key={ticker} value={ticker}>{ticker}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="outlined"
                      onClick={resetFilters}
                      fullWidth
                      size="small"
                      sx={{ fontFamily, mt: 3 }}
                    >
                      Reset Filters
                    </Button>
                  </Grid>
                </Grid>
                
                {/* Date Filter Row */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Date Filter</Typography>
                    <Select
                      value={dateFilterType}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      size="small"
                      sx={{ width: '100%', fontFamily, mb: 2 }}
                    >
                      <MenuItem value="all">All Documents</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="thisWeek">This Week</MenuItem>
                      <MenuItem value="lastWeek">Last Week</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </Grid>
                  
                  {dateFilterType === 'custom' && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Start Date</Typography>
                        <DatePicker
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: '100%', fontFamily, mb: 2 }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>End Date</Typography>
                        <DatePicker
                          value={endDate}
                          onChange={(newValue) => setEndDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: '100%', fontFamily, mb: 2 }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12} sm={3}>
                    <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Documents Found</Typography>
                    <Typography sx={{ fontFamily, fontSize: { xs: 14, sm: 16 }, color: '#666', mt: 1 }}>
                      {filteredDocuments.length} documents
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Document selector in its own row for better width */}
                <Box sx={{ mt: 2 }}>
                  <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 }, mb: 1 }}>PDF Document</Typography>
                  <Select
                    value={selectedDocId || ''}
                    onChange={(e) => setSelectedDocId(e.target.value || null)}
                    size="small"
                    fullWidth
                    sx={{ fontFamily }}
                  >
                    <MenuItem value="">Select PDF document...</MenuItem>
                    {filteredDocuments.map(doc => (
                      <MenuItem key={doc.id} value={doc.id} sx={{ 
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        lineHeight: 1.4,
                        padding: '8px 16px'
                      }}>
                        {doc.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Paper>

              {selectedDoc && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', width: '100%' }}>
                  <Typography variant="h6" sx={{ 
                    fontFamily, 
                    color: '#1A2341', 
                    mb: 2,
                    direction: language === 'Hebrew' ? 'rtl' : 'ltr',
                    textAlign: language === 'Hebrew' ? 'right' : 'left',
                    unicodeBidi: 'plaintext',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    {selectedDoc.title}
                  </Typography>
                  
                  {selectedDoc.storage_url && (
                    <PDFViewer
                      storageUrl={selectedDoc.storage_url}
                      documentTitle={selectedDoc.title}
                      language={language}
                      highlightPage={highlightPage}
                      highlightBbox={highlightBbox}
                    />
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight={600} sx={{ fontFamily }}>Language:</Typography>
                    <Select
                      value={language}
                      onChange={e => setLanguage(e.target.value as Language)}
                      size="small"
                      sx={{ width: 120, fontFamily }}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Hebrew">Hebrew</MenuItem>
                    </Select>
                    
                    <Typography fontWeight={600} sx={{ fontFamily }}>Provider:</Typography>
                    <Select
                      value={provider}
                      onChange={e => setProvider(e.target.value as string)}
                      size="small"
                      sx={{ width: 120, fontFamily }}
                    >
                      <MenuItem value="gemini">Gemini</MenuItem>
                    </Select>
                    
                    <Chip
                      label={useV3Rag ? "V3 RAG: ON" : "V3 RAG: OFF"}
                      color={useV3Rag ? "success" : "default"}
                      onClick={() => setUseV3Rag(!useV3Rag)}
                      sx={{ fontFamily, cursor: 'pointer' }}
                    />
                    
                    <Button
                      variant="outlined"
                      onClick={clearChat}
                      size="small"
                      sx={{ fontFamily, ml: 2 }}
                    >
                      Clear Chat
                    </Button>
                  </Box>

                  {/* AI Suggestions */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontFamily, color: '#1A2341' }}>
                        {language === 'Hebrew' ? 'הצעות שאלות' : 'Question Suggestions'}
                      </Typography>
                      {loadingSuggestions && (
                        <CircularProgress size={20} />
                      )}
                      {!loadingSuggestions && suggestions.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={generateSuggestions}
                          sx={{ fontFamily, ml: 'auto' }}
                        >
                          {language === 'Hebrew' ? 'רענן הצעות' : 'Regenerate'}
                        </Button>
                      )}
                    </Box>
                    
                    {suggestions.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {suggestions.map((suggestion, index) => (
                          <Paper
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{
                              p: { xs: 1, sm: 1.5 },
                              bgcolor: '#E3F2FD',
                              borderRadius: 2,
                              cursor: 'pointer',
                              fontFamily,
                              '&:hover': {
                                bgcolor: '#BBDEFB',
                              },
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              direction: containsHebrew(suggestion) ? 'rtl' : 'ltr',
                              textAlign: containsHebrew(suggestion) ? 'right' : 'left',
                              unicodeBidi: 'plaintext'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily,
                                lineHeight: 1.4,
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                            >
                              {suggestion}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Summary Button */}
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={generateSummary}
                      disabled={loadingSummary || !selectedDocId}
                      sx={{
                        fontFamily,
                        bgcolor: '#2B3A67',
                        color: '#fff',
                        fontWeight: 700,
                        minWidth: '200px',
                        '&:hover': {
                          bgcolor: '#1A2341'
                        }
                      }}
                    >
                      {loadingSummary ? (
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                      ) : (
                        language === 'Hebrew' ? 'סיכום מקצועי למנהל תיק השקעות' : 'Professional Portfolio Manager Summary'
                      )}
                    </Button>
                  </Box>

                  {/* Chat Input */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={language === 'Hebrew' ? 'שאל שאלה על המסמך...' : 'Ask a question about the document...'}
                      variant="outlined"
                      sx={{ fontFamily }}
                      disabled={loading}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={askQuestion}
                        disabled={loading || !question.trim() || !selectedDocId}
                        sx={{ fontFamily, bgcolor: '#1A2341', color: '#fff', fontWeight: 700 }}
                      >
                        {loading ? (
                          <CircularProgress size={20} sx={{ color: '#fff' }} />
                        ) : (
                          language === 'Hebrew' ? 'שאל שאלה' : 'Ask Question'
                        )}
                      </Button>
                    </Box>
                  </Box>

                  {/* Enhanced Error Display */}
                  {error && (
                    <Box sx={{ mt: 2 }}>
                      <Alert 
                        severity={structuredError?.type === 'timeout' ? 'warning' : 'error'} 
                        sx={{ mb: 1 }}
                        action={
                          showRetryButton && (
                            <Button 
                              color="inherit" 
                              size="small" 
                              onClick={handleRetry}
                              disabled={retryCountdown > 0}
                            >
                              {retryCountdown > 0 ? `Retry in ${retryCountdown}s` : 'Retry Now'}
                            </Button>
                          )
                        }
                      >
                        {error}
                        {structuredError?.type === 'timeout' && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {language === 'Hebrew' 
                              ? 'הבקשה לקחה יותר מדי זמן. נסה שוב או בדוק את החיבור שלך.'
                              : 'Request took too long. Try again or check your connection.'
                            }
                          </Typography>
                        )}
                        {structuredError?.type === 'rate_limit' && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {language === 'Hebrew' 
                              ? 'יותר מדי בקשות. אנא המתן לפני שתנסה שוב.'
                              : 'Too many requests. Please wait before trying again.'
                            }
                          </Typography>
                        )}
                      </Alert>
                      
                      {/* Loading indicator for retry countdown */}
                      {retryCountdown > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" sx={{ fontFamily }}>
                            {language === 'Hebrew' 
                              ? `נסיון חוזר בעוד ${retryCountdown} שניות...`
                              : `Retrying in ${retryCountdown} seconds...`
                            }
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Chat History */}
              {chatHistory.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', width: '100%' }}>
                  <Typography variant="h6" sx={{ fontFamily, color: '#1A2341', mb: 2 }}>
                    {language === 'Hebrew' ? 'היסטוריית שיחה' : 'Chat History'}
                  </Typography>
                  
                  {chatHistory.map((message) => (
                    <Box key={message.id} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip 
                          label="Q" 
                          size="small" 
                          color="primary" 
                          sx={{ fontFamily, fontWeight: 'bold' }}
                        />
                        <Typography variant="caption" sx={{ fontFamily, color: 'text.secondary' }}>
                          {formatDate(message.timestamp)}
                        </Typography>
                        {message.processing_time && (
                          <Typography variant="caption" sx={{ fontFamily, color: 'text.secondary' }}>
                            ({message.processing_time.toFixed(2)}s)
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1
                      }}>
                        {renderTextWithRTL(message.question)}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip 
                          label="A" 
                          size="small" 
                          color="success" 
                          sx={{ fontFamily, fontWeight: 'bold' }}
                        />
                      </Box>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#e8f5e8', 
                        borderRadius: 1
                      }}>
                        {renderTextWithRTL(message.answer)}
                      </Box>
                      
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
} 